import { Button, Flex, Tabs, type TabsProps } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FilterOption } from '@shared/lib/types';
import type { DateFilterValue } from '@shared/ui/vanilla-calendar/lib/types';
import { PeriodShortSelector } from '@shared/ui/selector/period-short-select/period-short-select';
import { Text } from '@shared/ui/antd';
import { MaterialDownload } from '@shared/assets/icons/components';
import { PageHeader } from '@shared/ui/page-header';

import { type PeriodPreset } from './lib/constants';
import {
  buildDefaultMonth,
  buildDefaultToday,
  buildDefaultWeek,
  buildDefaultYesterday,
} from './lib/utils';
import { HistoryTable } from './ui/history-table/history-table';
import { PlansGrid } from './ui/plans-grid/plans-grid';
import { TransactionsTable } from './ui/transactions-table/transactions-table';
import styles from './billing.module.scss';

export const Billing = () => {
  const { t: tBilling } = useTranslation('translation', { keyPrefix: 'billing' });
  const { t: tCommon } = useTranslation('translation');

  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('today');
  const [periodValue, setPeriodValue] = useState<FilterOption<DateFilterValue>[] | null>(() =>
    buildDefaultToday(tCommon('common.date.today')),
  );

  const periodTabs: TabsProps['items'] = useMemo(
    () => [
      { key: 'today', label: tCommon('common.date.today') },
      { key: 'yesterday', label: tCommon('common.date.yesterday') },
      { key: 'week', label: tCommon('common.calendar.lastWeek') },
      { key: 'month', label: tCommon('common.calendar.lastMonth') },
    ],
    [tCommon],
  );

  const handlePeriodPresetChange = useCallback(
    (next: PeriodPreset) => {
      setPeriodPreset(next);
      if (next === 'today') {
        setPeriodValue(buildDefaultToday(tCommon('common.date.today')));
        return;
      }
      if (next === 'yesterday') {
        setPeriodValue(buildDefaultYesterday(tCommon('common.date.yesterday')));
        return;
      }
      if (next === 'month') {
        setPeriodValue(buildDefaultMonth(tCommon('common.calendar.lastMonth')));
        return;
      }
      setPeriodValue(buildDefaultWeek(tCommon('common.calendar.lastWeek')));
    },
    [tCommon],
  );

  const handlePeriodChange = useCallback((val: FilterOption<DateFilterValue>[]) => {
    setPeriodValue(val);
  }, []);

  return (
    <Flex vertical gap={20} className={styles.root}>
      <PageHeader title={tBilling('title')} />

      <Flex vertical gap={24} className={styles.container}>
        <Flex vertical gap={4} className={styles.balance}>
          <Text variant="lg" fontWeight={500}>
            {tBilling('balance.label')}:{' '}
            <span className={styles.balanceValue}>{tBilling('balance.placeholder')}</span>
          </Text>
          <Text variant="sm" className={styles.balanceHint}>
            {tBilling('balance.hint')}
          </Text>
        </Flex>

        <PlansGrid />

        <TransactionsTable />

        {/* <Flex vertical gap={16} className={styles.history}>
          <Text variant="lg" fontWeight={500}>
            {tBilling('history.title')}
          </Text>

          <Flex align="center" justify="space-between" className={styles.historyControls}>
            <Tabs
              className={styles.periodTabs}
              activeKey={periodPreset}
              onChange={(key) => handlePeriodPresetChange(key as PeriodPreset)}
              items={periodTabs}
            />

            <Flex align="center" gap={12} className={styles.historyActions}>
              <PeriodShortSelector value={periodValue} onChange={handlePeriodChange} />
              <Button
                className={styles.downloadButton}
                type="default"
                iconPosition="end"
                icon={<MaterialDownload />}
              >
                {tBilling('history.export.csv')}
              </Button>
              <Button
                className={styles.downloadButton}
                type="default"
                iconPosition="end"
                icon={<MaterialDownload />}
              >
                {tBilling('history.export.xls')}
              </Button>
            </Flex>
          </Flex>

          <HistoryTable />
        </Flex> */}
      </Flex>
    </Flex>
  );
};
